import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import styles from './SessionHistoryDetailView.module.css';
import SessionDetailedViewPage from './SessionDetailedViewPage';

const AddendumView = ({
  addendumText,
  onAddendumTextChange,
  autoResizeTextarea,
  addendumNumber,
  addendumTextError,
  canEditAddendum = false,
  addendumData = [],
  session = null,
  sessionFormData = null,
  // Follow-Up specific props for expandable sections
  readOnlySections = [],
  expandedSections = [],
  toggleSection = () => {},
  formatTimeString = () => "",
  capitalizeFirstChar = (str) => str || "",
  durationDisplay = "",
  clientName = "",
  providerName = "",
  appointmentDate = null,
  appointmentTime = "",
  formatDate = () => ""
}) => {
  const [showProgressNote, setShowProgressNote] = useState(false);
  // Get latest addendum text when canEditAddendum is true
  const getLatestAddendumText = () => {
    if (!canEditAddendum || !addendumData || addendumData.length === 0) {
      return "";
    }
    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];
    if (latestAddendum &&
      latestAddendum.addendumSigned === false &&
      latestAddendum.addendumSubmit === true) {
      return latestAddendum.addendumText || "";
    }
    return "";
  };

  const displayText = canEditAddendum ? getLatestAddendumText() : addendumText;

  const handleProgressNoteClick = () => {
    setShowProgressNote(!showProgressNote);
  };

  return (
    <div className={styles.addendumContainer}>
      <Card className={`${styles.card} ${styles.addendumProgressNoteCard}`} >
        <div className={styles.addendumHeader}>
          <h3 className={`${styles.sectionHeading} ${styles.addendumHeading}`}>
            Progress Note
          </h3>
          <div
            className={styles.addendumIconContainer}
            onClick={handleProgressNoteClick}
            style={{ cursor: 'pointer' }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: showProgressNote ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
            >
              <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </Card>

      {/* Show Follow-Up sections below when showProgressNote is true */}
      {showProgressNote && session && sessionFormData && (
        <>
          {readOnlySections && readOnlySections.length > 0 ? (
            // Show Follow-Up sections if readOnlySections are provided
            // Always expanded, no toggle functionality when shown from progress note
            <Card className={styles.card}>
              <div className={styles.collapsibleStack}>
                {readOnlySections.map(({ id, title, content }) => {
                  return (
                    <div id={id} key={id} className={styles.collapsibleSection}>
                      <div
                        className={styles.collapsibleHeader}
                        style={{ cursor: 'default', pointerEvents: 'none' }}
                      >
                        <span>{title}</span>
                      </div>
                      <div
                        id={`${id}-content`}
                        className={styles.collapsibleBodyExpanded}
                      >
                        {content}
                        <div className={styles.collapsibleSeparator} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : (
            // Show initial assessment sections if readOnlySections are not provided (for SessionHistoryDetailView)
            <SessionDetailedViewPage
              sessionFormData={sessionFormData}
              session={session}
              addendumData={addendumData}
              disableToggle={true}
            />
          )}
          
          {/* Show signed addendums below sections when showProgressNote is true */}
          {addendumData && Array.isArray(addendumData) && addendumData.length > 0 && (
            <>
              {addendumData.map((addendum, index) => {
                // Only show addendums that are signed and have addendumText
                if (addendum.addendumSigned === true && addendum.addendumText && addendum.addendumText.trim() !== "") {
                  const addendumNumber = index + 1;
                  return (
                    <Card key={`addendum-${index}`} id={`addendum-${index}`} className={styles.card}>
                      <div className={styles.collapsibleStack}>
                        <div className={styles.collapsibleSection}>
                          <div style={{ paddingTop: "20px" }}>
                            <h3 className={styles.sectionHeading} style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>
                              Addendum # {addendumNumber}
                            </h3>
                            <div style={{ marginBottom: "16px" }}>
                              <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "8px" }}>
                                Provide addendum or correction details:
                              </label>
                              <div style={{
                                fontSize: "16px",
                                fontWeight: "400",
                                color: "#000",
                                padding: "12px",
                                borderRadius: "4px",
                                backgroundColor: "transparent",
                                minHeight: "60px",
                                lineHeight: "1.5",
                                textAlign: "left",
                                whiteSpace: "pre-wrap",
                                wordWrap: "break-word"
                              }}>
                                {addendum.addendumText || ""}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                }
                return null;
              })}
            </>
          )}
        </>
      )}

      <Card className={styles.card}>
        <h3 className={`${styles.sectionHeading} ${styles.addendumTitle}`}>
          {addendumNumber || "Addendum"}
        </h3>
        <p className={styles.addendumDescription}>
          Provide addendum or correction details
        </p>
        {canEditAddendum ? (
          <div className={styles.addendumDisplayText}>
            {displayText}
          </div>
        ) : (
          <>
            <textarea
              id="addendum-textarea"
              value={addendumText || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                onAddendumTextChange(e.target.value);
              }}
              placeholder="Free-text"
              className={`${styles.textarea} ${styles.addendumTextarea}`}
              rows={6}
            />
            {addendumTextError && (
              <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                {addendumTextError}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AddendumView;

